﻿﻿/*
  Shopify Store Locator
  ---------------------
  - Les IDs du DOM sont générés à partir du `sectionId` Liquid.
  - Les classes CSS générées ici sont stylées dans `assets/store-locator.css`.
  - Flux : chargement des scripts -> init map/libs -> liaison UI -> chargement des magasins -> recherche.
*/

document.addEventListener('DOMContentLoaded', () => {
  const config = window.storeLocatorConfig;
  if (!config) return;

  // ---------------------------------------------------------------------------
  // 1) Configuration + références DOM
  // ---------------------------------------------------------------------------
  const { sectionId, jsonUrl, snakeUrl, apiKey } = config;
  const i18n = config.i18n || {};
  const sectionRoot = document.getElementById(`store-locator-${sectionId}`);
  const elements = {
    map: document.getElementById(`google-map-${sectionId}`),
    list: document.getElementById(`store-list-${sectionId}`),
    searchBtn: document.getElementById(`search-btn-${sectionId}`),
    geoBtn: document.getElementById(`geolocation-btn-${sectionId}`),
    radius: document.getElementById(`radius-select-${sectionId}`),
    mobileRadiusBtn: document.getElementById(`mobile-radius-btn-${sectionId}`),
    overlay: document.getElementById(`map-overlay-${sectionId}`),
    overlayGeo: document.getElementById(`overlay-geoloc-btn-${sectionId}`),
    overlaySearch: document.getElementById(`overlay-search-btn-${sectionId}`),
    input: document.getElementById(`location-input-${sectionId}`)
  };
  if (!elements.map || !elements.list) return;

  // ---------------------------------------------------------------------------
  // 2) État d'exécution + constantes
  // ---------------------------------------------------------------------------
  const state = {
    map: null,
    infoWindow: null,
    markers: [],
    clusterer: null,
    stores: [],
    placeAutocomplete: null,
    selectedPlace: null,
    geocoder: null,
    lastLocation: null,
    AdvancedMarkerElement: null,
    PlaceAutocompleteElement: null,
    cameraAnimationToken: 0,
    cameraTimeoutId: null
  };

  const defaultCenter = { lat: 46.603354, lng: 1.888334 };
  const MAX_RADIUS_KM = 25;
  const CLUSTER_MAX_ZOOM = 12;
  const MARKER_FOCUS_ZOOM = 14;
  const MARKER_FOCUS_OFFSET_PX = {
    desktop: -72,
    desktopNoName: -100,
    mobile: -100,
    mobileNoName: -150
  };
  const CLUSTER_TIER_ZOOM = {
    danger: 12,
    canary: 13,
    electric: 14
  };
  const sanitizeSectionId = sectionId.replace(/[^a-zA-Z0-9]/g, '');
  const callbackName = `initMap_${sanitizeSectionId}`;

  // ---------------------------------------------------------------------------
  // 3) Primitives UI
  // ---------------------------------------------------------------------------
  const showMessage = (title, body) => {
    elements.list.innerHTML = `
      <div class="no-results">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
          <path d="M12 8V12M12 16H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
        <p>${title}</p>
        ${body ? `<p class="small">${body}</p>` : ''}
      </div>
    `;
  };

  const showLoading = (message = i18n.loading_stores) => {
    elements.list.innerHTML = `
      <div class="loading-placeholder">
        <div class="spinner"></div>
        <p>${message}</p>
      </div>
    `;
  };

  const toggleButtonLoading = (button, isLoading) => {
    if (!button) return;
    button.classList.toggle('loading', isLoading);
    button.disabled = isLoading;
    button.setAttribute('aria-busy', String(isLoading));
  };

  const setActionLoading = (buttons, isLoading, message) => {
    buttons.forEach((button) => toggleButtonLoading(button, isLoading));
    if (isLoading && message) showLoading(message);
  };

  const hideOverlay = () => {
    if (elements.overlay) {
      elements.overlay.classList.add('hidden');
    }
  };

  const getInputValue = () => {
    if (state.placeAutocomplete) {
      const directValue = state.placeAutocomplete.value;
      if (typeof directValue === 'string' && directValue.trim()) return directValue.trim();
      const attrValue = state.placeAutocomplete.getAttribute('value');
      if (attrValue && attrValue.trim()) return attrValue.trim();
    }
    return elements.input ? elements.input.value.trim() : '';
  };

  // ---------------------------------------------------------------------------
  // 4) Calculs + helpers de marqueurs
  // ---------------------------------------------------------------------------
  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2
      + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
    return R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
  };

  const getClusterClass = (count) => {
    const sizeClass = count >= 20 ? 'cluster-marker--lg' : count >= 10 ? 'cluster-marker--md' : 'cluster-marker--sm';
    const colorClass = count >= 20
      ? 'cluster-marker--danger'
      : count >= 10
        ? 'cluster-marker--canary'
        : count >= 2
          ? 'cluster-marker--electric'
          : '';
    return `cluster-marker ${sizeClass} ${colorClass}`.trim();
  };

  const isNoNameStore = (store) => Boolean(store && store.name && store.name.toUpperCase().includes('NO NAME'));

  const PIN_PATH = '<path d="M128 252.6C128 148.4 214 64 320 64C426 64 512 148.4 512 252.6C512 371.9 391.8 514.9 341.6 569.4C329.8 582.2 310.1 582.2 298.3 569.4C248.1 514.9 127.9 371.9 127.9 252.6zM320 320C355.3 320 384 291.3 384 256C384 220.7 355.3 192 320 192C284.7 192 256 220.7 256 256C256 291.3 284.7 320 320 320z"/>';
  const PIN_BADGE = '<circle cx="320" cy="256" r="120" fill="#ffffff" stroke="#111111" stroke-width="32"/><line x1="200" y1="360" x2="420" y2="150" stroke="#111111" stroke-width="64" stroke-linecap="round"/>';

  // Tailles gérées en CSS (`.store-marker`), pas dans les SVG.
  const MARKER_SVG = {
    default: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" aria-hidden="true">${PIN_PATH}</svg>`,
    noname: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" aria-hidden="true">${PIN_PATH}${PIN_BADGE}</svg>`,
    snake: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 22 31.23" aria-hidden="true"><path d="M22,11c0,10.06-11,20.23-11,20.23,0,0-11-10.77-11-20.23C0,4.92,4.92,0,11,0s11,4.92,11,11Z"/><path fill="#f5f5f5" d="M3.06,16.11v-.79c2.2.27,4.53.43,6.98.43,4.05,0,5.79-.41,5.79-2.09,0-.51-.21-.93-.82-1.2-.79-.35-2.12-.47-4.88-.63-2.93-.17-4.42-.41-5.38-.95-.98-.55-1.41-1.35-1.41-2.48,0-2.85,2.83-3.69,7.91-3.69,2.45,0,4.54.22,6.44.54v.79c-1.93-.25-3.89-.43-6.05-.43-3.83,0-5.21.49-5.21,1.84,0,.46.13.85.62,1.11.66.36,1.82.51,4.65.66,2.98.17,4.57.4,5.67.92,1.16.57,1.55,1.39,1.55,2.6,0,3.09-3.1,3.94-8.5,3.94-2.82,0-5.21-.25-7.38-.55Z"/></svg>'
  };

  /*
    TEMPORAIRE — opération Snake Kiss : la boutique en propre porte le picto
    Snake Kiss à la place de son marqueur habituel. Repasser à false à la fin de
    l'opération pour lui rendre son pin badgé (rien d'autre à défaire).
  */
  const SNAKE_KISS_NONAME_MARKER = true;

  // `snake: true` dans le JSON des boutiques = revendeur de la capsule Snake Kiss.
  // Les boutiques en propre passent avant, pour qu'un réimport du CSV qui les
  // flaggerait ne leur fasse pas perdre leur marqueur.
  const getMarkerVariant = (store) => {
    if (isNoNameStore(store)) return SNAKE_KISS_NONAME_MARKER ? 'snake' : 'noname';
    return store && store.snake === true ? 'snake' : 'default';
  };

  const createStoreMarkerContent = (variant) => {
    const container = document.createElement('div');
    container.className = `store-marker store-marker--${variant}`;
    container.innerHTML = MARKER_SVG[variant];
    return container;
  };

  // ---------------------------------------------------------------------------
  // 5) Rendu (liste, marqueurs, infowindow)
  // ---------------------------------------------------------------------------
  const clearMarkers = () => {
    if (state.clusterer) {
      state.clusterer.clearMarkers();
      state.clusterer = null;
    }
    state.markers.forEach((marker) => {
      marker.map = null;
    });
    state.markers = [];
  };

  const projectLatLng = ({ lat, lng }, zoom) => {
    const scale = 256 * 2 ** zoom;
    const siny = Math.min(Math.max(Math.sin((lat * Math.PI) / 180), -0.9999), 0.9999);
    return {
      x: ((lng + 180) / 360) * scale,
      y: (0.5 - (Math.log((1 + siny) / (1 - siny)) / (4 * Math.PI))) * scale
    };
  };

  const unprojectLatLng = ({ x, y }, zoom) => {
    const scale = 256 * 2 ** zoom;
    const lng = (x / scale) * 360 - 180;
    const mercatorY = 0.5 - (y / scale);
    const lat = (2 * Math.atan(Math.exp(mercatorY * 2 * Math.PI)) - (Math.PI / 2)) * (180 / Math.PI);
    return { lat, lng };
  };

  const getOffsetCenter = (position, offsetX, offsetY, zoom) => {
    const projected = projectLatLng(position, zoom);
    return unprojectLatLng({
      x: projected.x + offsetX,
      y: projected.y + offsetY
    }, zoom);
  };

  const toLatLngLiteral = (position) => {
    if (!position) return null;
    if (typeof position.lat === 'function' && typeof position.lng === 'function') {
      return { lat: position.lat(), lng: position.lng() };
    }
    return position;
  };

  const cancelCameraAnimation = () => {
    state.cameraAnimationToken += 1;
    if (state.cameraTimeoutId) {
      window.clearTimeout(state.cameraTimeoutId);
      state.cameraTimeoutId = null;
    }
  };

  const animateMapView = ({ center, zoom, onComplete }) => {
    if (!state.map) return;

    const map = state.map;
    cancelCameraAnimation();
    const targetCenter = toLatLngLiteral(center) || toLatLngLiteral(map.getCenter());
    const currentZoom = map.getZoom();
    const targetZoom = Number.isFinite(zoom) ? Math.max(1, Math.round(zoom)) : currentZoom;

    if (!targetCenter || !Number.isFinite(currentZoom) || !Number.isFinite(targetZoom)) return;

    if (currentZoom === targetZoom) {
      map.panTo(targetCenter);
      if (typeof onComplete === 'function') onComplete();
      return;
    }

    map.panTo(targetCenter);
    state.cameraTimeoutId = window.setTimeout(() => {
      state.cameraTimeoutId = null;
      map.setZoom(targetZoom);
      if (typeof onComplete === 'function') onComplete();
    }, 120);
  };

  const getMarkerPosition = (marker) => {
    if (!marker || !marker.position) return null;
    if (typeof marker.position.lat === 'function' && typeof marker.position.lng === 'function') {
      return { lat: marker.position.lat(), lng: marker.position.lng() };
    }
    return marker.position;
  };

  const focusMarkerOnMap = (marker, noNameStore) => {
    const markerPosition = getMarkerPosition(marker);
    if (!markerPosition) return;

    const isMobile = window.innerWidth <= 1024;
    const offsetY = isMobile
      ? (noNameStore ? MARKER_FOCUS_OFFSET_PX.mobileNoName : MARKER_FOCUS_OFFSET_PX.mobile)
      : (noNameStore ? MARKER_FOCUS_OFFSET_PX.desktopNoName : MARKER_FOCUS_OFFSET_PX.desktop);
    const adjustedCenter = getOffsetCenter(markerPosition, 0, offsetY, MARKER_FOCUS_ZOOM);
    const currentZoom = state.map ? state.map.getZoom() : null;

    if (!Number.isFinite(currentZoom)) return;

    if (Math.round(currentZoom) === MARKER_FOCUS_ZOOM) {
      cancelCameraAnimation();
      state.map.panTo(adjustedCenter);
      return;
    }

    animateMapView({
      center: markerPosition,
      zoom: MARKER_FOCUS_ZOOM,
      onComplete: () => {
        if (!state.map) return;
        state.map.panTo(adjustedCenter);
      }
    });
  };

  const zoomToCluster = (cluster) => {
    if (!state.map || !cluster) return;

    const targetZoom = cluster.count >= 20
      ? CLUSTER_TIER_ZOOM.danger
      : cluster.count >= 10
        ? CLUSTER_TIER_ZOOM.canary
        : CLUSTER_TIER_ZOOM.electric;

    animateMapView({
      center: cluster.position,
      zoom: targetZoom
    });
  };

  const openInfoWindow = (store, marker) => {
    const addressParts = [];
    if (store.address.line1) addressParts.push(store.address.line1);
    if (store.address.line2) addressParts.push(store.address.line2);
    const streetAddress = addressParts.join(', ');
    const cityLine = `${store.address.postal_code} ${store.address.city}`;
    const noNameStore = isNoNameStore(store);

    let content = `<div class="info-window">`;
    content += `
      <h3>${store.name}</h3>
      ${streetAddress ? `<p>${streetAddress}</p>` : ''}
      <p>${cityLine}</p>
      ${store.distance !== undefined ? `<p class="info-window__distance"><strong>${store.distance.toFixed(1)} km</strong> ${i18n.distance_from_position}</p>` : ''}
      <hr>
      ${noNameStore ? `
        <div class="info-window__hours">
          <p class="info-window__phone"><strong>${i18n.phone_label}: </strong><a href="tel:${i18n.noname_phone.replace(/\s/g, '')}">${i18n.noname_phone}</a></p>
          <hr>
          <p class="info-window__hours-heading"><strong>${i18n.our_hours}</strong></p>
          <hr>
          <div class="info-window__hours-list">
            <div class="info-window__hours-row"><span class="info-window__hours-day">${i18n.monday}</span><span class="info-window__hours-time">${i18n.closed}</span></div>
            <div class="info-window__hours-row"><span class="info-window__hours-day">${i18n.tuesday}</span><span class="info-window__hours-time">${i18n.hours_weekday}</span></div>
            <div class="info-window__hours-row"><span class="info-window__hours-day">${i18n.wednesday}</span><span class="info-window__hours-time">${i18n.hours_weekday}</span></div>
            <div class="info-window__hours-row"><span class="info-window__hours-day">${i18n.thursday}</span><span class="info-window__hours-time">${i18n.hours_weekday}</span></div>
            <div class="info-window__hours-row"><span class="info-window__hours-day">${i18n.friday}</span><span class="info-window__hours-time">${i18n.hours_weekday}</span></div>
            <div class="info-window__hours-row"><span class="info-window__hours-day">${i18n.saturday}</span><span class="info-window__hours-time">${i18n.hours_saturday}</span></div>
            <div class="info-window__hours-row"><span class="info-window__hours-day">${i18n.sunday}</span><span class="info-window__hours-time">${i18n.closed}</span></div>
          </div>
        </div>
      ` : ''}
      <a href="https://www.google.com/maps/dir//${encodeURIComponent(store.formatted_address)}" target="_blank" rel="noopener noreferrer" class="info-window__navigate">
        ${i18n.get_directions}
      </a>
    `;
    content += `</div>`;

    state.infoWindow.setContent(content);
    state.infoWindow.open(state.map, marker);
    focusMarkerOnMap(marker, noNameStore);
  };

  const renderResults = (stores) => {
    if (!stores.length) {
      showMessage(i18n.no_stores_found, i18n.increase_radius);
      return;
    }

    let html = `<div class="results-header"><strong>${stores.length}</strong> ${stores.length > 1 ? i18n.store_count_other : i18n.store_count_one}</div>`;

    stores.forEach((store, index) => {
      const addressParts = [];
      if (store.address.line1) addressParts.push(store.address.line1);
      if (store.address.line2) addressParts.push(store.address.line2);
      const streetAddress = addressParts.join(', ');
      const cityLine = `${store.address.postal_code} ${store.address.city}`;
      const distanceText = store.distance ? `${store.distance.toFixed(1)} km` : '';

      html += `
        <div class="store-item" data-store-index="${index}">
          <div class="store-item-header">
            <span class="store-number">${index + 1}</span>
            <div class="store-info">
              <h3 class="store-name">${store.name}</h3>
              ${distanceText ? `<div class="store-distance">${distanceText}</div>` : ''}
            </div>
          </div>
          <div class="store-address">
            ${streetAddress ? `<p>${streetAddress}</p>` : ''}
            <p>${cityLine}</p>
          </div>
          <div class="store-actions">
            <button class="view-on-map-btn" data-store-index="${index}">${i18n.view_on_map}</button>
            <a href="https://www.google.com/maps/dir//${encodeURIComponent(store.formatted_address)}" target="_blank" rel="noopener noreferrer" class="navigate-btn">${i18n.directions}</a>
          </div>
        </div>
      `;
    });

    elements.list.innerHTML = html;
    document.querySelectorAll(`#store-list-${sectionId} [data-store-index]`).forEach((node) => {
      node.addEventListener('click', (event) => {
        event.stopPropagation();
        const index = Number(node.dataset.storeIndex);
        if (state.markers[index]) {
          openInfoWindow(stores[index], state.markers[index]);
          if (window.innerWidth <= 1024) {
            elements.map.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      });
    });
  };

  const renderMarkers = (stores) => {
    clearMarkers();
    const markers = stores.map((store) => {
      const marker = new state.AdvancedMarkerElement({
        position: { lat: store.coordinates.lat, lng: store.coordinates.lng },
        map: state.map,
        title: store.name,
        content: createStoreMarkerContent(getMarkerVariant(store))
      });
      marker.addListener('click', () => openInfoWindow(store, marker));
      return marker;
    });

    const renderer = {
      render({ count, position }) {
        const clusterElement = document.createElement('div');
        clusterElement.className = getClusterClass(count);
        clusterElement.innerHTML = `<span>${count}</span>`;
        return new state.AdvancedMarkerElement({
          position,
          content: clusterElement,
          zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count
        });
      }
    };

    const algorithm = markerClusterer.SuperClusterAlgorithm
      ? new markerClusterer.SuperClusterAlgorithm({ maxZoom: CLUSTER_MAX_ZOOM })
      : undefined;

    state.clusterer = new markerClusterer.MarkerClusterer({
      map: state.map,
      markers,
      renderer,
      algorithm,
      onClusterClick: (_, cluster) => {
        zoomToCluster(cluster);
      }
    });
    state.markers = markers;
  };

  const fitMap = (stores, userLocation) => {
    if (!stores.length) {
      state.map.setCenter(defaultCenter);
      state.map.setZoom(6);
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    if (userLocation) bounds.extend(userLocation);
    stores.forEach((store) => bounds.extend({ lat: store.coordinates.lat, lng: store.coordinates.lng }));
    state.map.fitBounds(bounds);
    const listener = google.maps.event.addListener(state.map, 'idle', () => {
      if (state.map.getZoom() > 15) state.map.setZoom(15);
      google.maps.event.removeListener(listener);
    });
  };

  // ---------------------------------------------------------------------------
  // 6) Actions de recherche + géolocalisation
  // ---------------------------------------------------------------------------
  const searchFromLocation = (location) => {
    state.lastLocation = location;
    const radiusValue = elements.radius ? elements.radius.value : String(MAX_RADIUS_KM);
    const radius = Math.min(parseFloat(radiusValue || String(MAX_RADIUS_KM)), MAX_RADIUS_KM);

    const nearby = state.stores
      .filter((store) => store.coordinates && store.coordinates.lat && store.coordinates.lng)
      .map((store) => ({
        ...store,
        distance: calculateDistance(location.lat(), location.lng(), store.coordinates.lat, store.coordinates.lng)
      }))
      .filter((store) => store.distance <= radius)
      .sort((a, b) => {
        const aNoName = isNoNameStore(a);
        const bNoName = isNoNameStore(b);
        if (aNoName !== bNoName) return aNoName ? -1 : 1;
        return a.distance - b.distance;
      });

    renderResults(nearby);
    renderMarkers(nearby);
    fitMap(nearby, location);
  };

  const performSearch = () => {
    if (!state.stores.length) return;

    if (state.selectedPlace && state.selectedPlace.location) {
      const selectedLocation = state.selectedPlace.location;
      setActionLoading([elements.searchBtn], true, i18n.searching_nearby);
      state.selectedPlace = null;
      hideOverlay();
      searchFromLocation(selectedLocation);
      setActionLoading([elements.searchBtn], false);
      return;
    }

    const address = getInputValue();
    if (!address) {
      showMessage(i18n.enter_address, i18n.also_use_location);
      return;
    }

    setActionLoading([elements.searchBtn], true, i18n.processing_address);
    state.geocoder.geocode({ address, componentRestrictions: { country: "FR" } }, (results, status) => {
      setActionLoading([elements.searchBtn], false);
      if (status === "OK" && results[0]) {
        hideOverlay();
        searchFromLocation(results[0].geometry.location);
      } else {
        showMessage(i18n.address_not_found, i18n.please_retry);
      }
    });
  };

  const useGeolocation = () => {
    if (!navigator.geolocation) {
      showMessage(i18n.geolocation_unavailable, i18n.enter_address_hint);
      return;
    }

    setActionLoading(
      [elements.geoBtn, elements.overlayGeo],
      true,
      i18n.locating
    );

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location = new google.maps.LatLng(position.coords.latitude, position.coords.longitude);
        hideOverlay();
        searchFromLocation(location);
        setActionLoading([elements.geoBtn, elements.overlayGeo], false);

        state.geocoder.geocode({ location }, (results, status) => {
          if (status === 'OK' && results[0]) {
            const address = results[0].formatted_address;
            if (state.placeAutocomplete) {
              const shadowInput = state.placeAutocomplete.shadowRoot
                ? state.placeAutocomplete.shadowRoot.querySelector('input')
                : null;
              if (shadowInput) {
                shadowInput.value = address;
              } else {
                state.placeAutocomplete.value = address;
              }
            } else if (elements.input) {
              elements.input.value = address;
            }
          }
        });
      },
      () => {
        showMessage(i18n.location_error, i18n.enter_address_hint);
        setActionLoading([elements.geoBtn, elements.overlayGeo], false);
      }
    );
  };

  // ---------------------------------------------------------------------------
  // 7) Champs de saisie + liaison des événements UI
  // ---------------------------------------------------------------------------
  const setupAutocomplete = () => {
    if (!state.PlaceAutocompleteElement || !elements.input) return;

    const placeAutocomplete = new state.PlaceAutocompleteElement({
      componentRestrictions: { country: 'fr' }
    });
    elements.input.parentNode.replaceChild(placeAutocomplete, elements.input);
    state.placeAutocomplete = placeAutocomplete;

    placeAutocomplete.addEventListener('gmp-select', async ({ placePrediction }) => {
      setActionLoading([elements.searchBtn], true, i18n.processing_address);
      try {
        const place = placePrediction.toPlace();
        await place.fetchFields({ fields: ['displayName', 'formattedAddress', 'location'] });
        state.selectedPlace = place;
        performSearch();
      } catch (error) {
        setActionLoading([elements.searchBtn], false);
        showMessage(i18n.address_not_found, i18n.please_retry);
      }
    });
  };

  const bindUI = () => {
    if (elements.mobileRadiusBtn && elements.radius) {
      const radiusValues = ['5', '10', '15', '25'];
      let currentIndex = radiusValues.indexOf(elements.radius.value);
      if (currentIndex === -1) currentIndex = 3;

      elements.mobileRadiusBtn.addEventListener('click', () => {
        currentIndex = (currentIndex + 1) % radiusValues.length;
        elements.radius.value = radiusValues[currentIndex];
        const span = elements.mobileRadiusBtn.querySelector('span');
        if (span) span.textContent = i18n.radius_label.replace('{{ value }}', radiusValues[currentIndex]);
        if (state.lastLocation) searchFromLocation(state.lastLocation);
      });
    }

    if (elements.searchBtn) {
      elements.searchBtn.addEventListener('click', performSearch);
    }

    if (sectionRoot) {
      sectionRoot.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        const activeElement = document.activeElement;
        const placeAutocomplete = state.placeAutocomplete || sectionRoot.querySelector('gmp-place-autocomplete');

        if (placeAutocomplete) {
          let isInPlaceAutocomplete = false;
          if (activeElement === placeAutocomplete) isInPlaceAutocomplete = true;
          else if (placeAutocomplete.shadowRoot && placeAutocomplete.shadowRoot.contains(activeElement)) isInPlaceAutocomplete = true;
          else if (placeAutocomplete.contains && placeAutocomplete.contains(activeElement)) isInPlaceAutocomplete = true;
          else if (placeAutocomplete.shadowRoot) {
            const shadowInput = placeAutocomplete.shadowRoot.querySelector('input');
            if (shadowInput && (event.target === shadowInput || activeElement === shadowInput)) isInPlaceAutocomplete = true;
          }

          if (isInPlaceAutocomplete) {
            event.preventDefault();
            event.stopPropagation();
            performSearch();
          }
        } else if (elements.input && (activeElement === elements.input || event.target === elements.input)) {
          event.preventDefault();
          event.stopPropagation();
          performSearch();
        }
      }, true);
    }

    if (elements.radius) {
      elements.radius.addEventListener('change', () => {
        if (state.lastLocation) searchFromLocation(state.lastLocation);
      });
    }

    if (elements.geoBtn) elements.geoBtn.addEventListener('click', useGeolocation);

    if (elements.overlayGeo) {
      elements.overlayGeo.addEventListener('click', () => {
        useGeolocation();
      });
    }

    if (elements.overlaySearch) {
      elements.overlaySearch.addEventListener('click', () => {
        hideOverlay();
        setTimeout(() => {
          if (state.placeAutocomplete) {
            const shadowInput = state.placeAutocomplete.shadowRoot
              ? state.placeAutocomplete.shadowRoot.querySelector('input')
              : null;
            if (shadowInput) shadowInput.focus();
            else state.placeAutocomplete.focus();
          } else if (elements.input) {
            elements.input.focus();
          }
        }, 100);
      });
    }
  };

  // ---------------------------------------------------------------------------
  // 8) Données + initialisation des APIs Google
  // ---------------------------------------------------------------------------
  const fetchJson = async (url) => {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  };

  const normalizeCp = (cp) => String(cp || '').trim().replace(/^0+/, '');
  const normalizeName = (name) => String(name || '').toUpperCase().replace(/[^A-Z0-9]+/g, '');

  /*
    Retrouve un de nos revendeurs dans la liste du client. Le nom exact ne suffit
    pas : le client tronque les noms à 20 caractères et y colle des suffixes
    ("FAMILY SAS", "SORELLE RAMONDA LIV"). À code postal égal, on accepte donc
    qu'un nom soit le préfixe de l'autre. Mesuré sur le fichier réel : 4 paires
    concernées sur 1043 boutiques, d'où le console.warn pour les rendre visibles.
  */
  const findClientEntry = (stores, extra) => {
    const wantedCp = normalizeCp(extra.address && extra.address.postal_code);
    const wantedName = normalizeName(extra.name);
    let partial = null;
    for (let i = 0; i < stores.length; i += 1) {
      const store = stores[i];
      if (normalizeCp(store.address && store.address.postal_code) !== wantedCp) continue;
      const name = normalizeName(store.name);
      if (name === wantedName) return { store, exact: true };
      if (!partial && name.length >= 4 && (name.startsWith(wantedName) || wantedName.startsWith(name))) {
        partial = store;
      }
    }
    return partial ? { store: partial, exact: false } : null;
  };

  /*
    Applique assets/store-locator-snake.json sur la liste du client, qui peut donc
    être réécrasée sans perdre l'info. Le rapprochement se fait sur `code`, avec le
    code postal en contrôle : si le client renumérote un jour ses boutiques, rien
    n'est flagué plutôt que de flaguer les mauvaises.
  */
  const applySnakeList = (stores, snakeList) => {
    if (!snakeList) return stores;

    const expected = new Map((snakeList.codes || []).map((entry) => [entry.code, normalizeCp(entry.cp)]));
    let matched = 0;
    stores.forEach((store) => {
      if (expected.get(store.code) === normalizeCp(store.address && store.address.postal_code)) {
        store.snake = true;
        matched += 1;
      }
    });
    if (matched !== expected.size) {
      console.warn(`[store-locator] ${matched}/${expected.size} revendeurs Snake Kiss retrouvés `
        + 'dans la liste des boutiques : codes obsolètes ou renumérotation côté client.');
    }

    // Revendeurs absents de la liste du client : ajoutés ici, et simplement flagués
    // le jour où le client les intègre lui-même, sous son propre code et son propre nom.
    (snakeList.boutiques || []).forEach((extra) => {
      const found = findClientEntry(stores, extra);
      if (!found) {
        stores.push(Object.assign({}, extra, { snake: true }));
        return;
      }
      found.store.snake = true;
      if (!found.exact) {
        console.warn(`[store-locator] "${extra.name}" rapproché de "${found.store.name}" `
          + '(même code postal, nom partiel) : à vérifier, puis à retirer du fichier snake.');
      }
    });
    return stores;
  };

  const loadStores = async () => {
    showLoading();
    try {
      const [stores, snakeList] = await Promise.all([
        fetchJson(jsonUrl),
        // Marqueur cosmétique : son absence ne doit pas empêcher la carte de fonctionner.
        snakeUrl ? fetchJson(snakeUrl).catch(() => null) : Promise.resolve(null)
      ]);
      state.stores = applySnakeList(stores || [], snakeList);
      showMessage(i18n.search_store, i18n.use_address_or_location);
    } catch (error) {
      showMessage(i18n.load_error, i18n.please_retry);
    }
  };

  window[callbackName] = async () => {
    const { Map, InfoWindow } = await google.maps.importLibrary('maps');
    const { AdvancedMarkerElement } = await google.maps.importLibrary('marker');
    const { PlaceAutocompleteElement } = await google.maps.importLibrary('places');

    state.AdvancedMarkerElement = AdvancedMarkerElement;
    state.PlaceAutocompleteElement = PlaceAutocompleteElement;
    state.map = new Map(elements.map, {
      zoom: 6,
      center: defaultCenter,
      mapTypeControl: false,
      fullscreenControl: false,
      zoomControl: true,
      mapId: '4fa3f93ca6a5b995d60aa29a'
    });
    state.infoWindow = new InfoWindow({ disableAutoPan: true });
    state.geocoder = new google.maps.Geocoder();

    if (elements.radius) {
      Array.from(elements.radius.options).forEach((option) => {
        if (parseFloat(option.value) > MAX_RADIUS_KM) option.remove();
      });
      if (parseFloat(elements.radius.value || '0') > MAX_RADIUS_KM) {
        elements.radius.value = String(MAX_RADIUS_KM);
      }
    }

    setupAutocomplete();
    bindUI();
    await loadStores();
  };

  const loadScript = (src, id) => new Promise((resolve, reject) => {
    const existing = document.getElementById(id);
    if (existing) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });

  const loadClusterer = () => {
    if (window.markerClusterer && window.markerClusterer.MarkerClusterer) {
      return Promise.resolve();
    }
    return loadScript(
      'https://unpkg.com/@googlemaps/markerclusterer/dist/index.min.js',
      `marker-clusterer-${sanitizeSectionId}`
    );
  };

  const loadMaps = () => {
    if (window.google && window.google.maps) return Promise.resolve();
    const src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=${callbackName}&loading=async&v=weekly`;
    return loadScript(src, `google-maps-${sanitizeSectionId}`);
  };

  const startMaps = () => {
    if (!window.googleMapsLoaded) {
      loadClusterer()
        .then(() => loadMaps())
        .catch(() => showMessage(i18n.map_load_error, i18n.please_retry));
      window.googleMapsLoaded = true;
    } else {
      loadClusterer()
        .then(() => {
          if (window[callbackName]) window[callbackName]();
        })
        .catch(() => showMessage(i18n.map_load_error, i18n.please_retry));
    }
  };

  // ---------------------------------------------------------------------------
  // 9) Consentement cookies — service "gmaps"
  // ---------------------------------------------------------------------------
  // Google Maps dépose des cookies tiers : normalement on ne charge le script
  // qu'après acceptation du service "gmaps". Tant que le consentement n'est pas
  // donné, l'overlay #map-consent reste affiché et invite à accepter.
  //
  // TEMPORAIRE — BYPASS CONSENTEMENT 
  // TODO : Passer BYPASS_MAP_CONSENT à false dès que le vendor "gmaps" est configuré
  // côté CookieHub, puis brancher `startMapsOnce()` sur son callback
  // (voir bloc `else` ci-dessous, conservé intact pour Axeptio).
  const BYPASS_MAP_CONSENT = true;

  const GMAPS_VENDOR = 'gmaps';
  const consentOverlay = document.getElementById(`map-consent-${sectionId}`);
  let mapsStarted = false;

  const startMapsOnce = () => {
    if (mapsStarted) return;
    mapsStarted = true;
    if (consentOverlay) consentOverlay.style.display = 'none';
    startMaps();
  };

  if (BYPASS_MAP_CONSENT) {
    // TEMPORAIRE : démarrage immédiat, sans attendre le consentement.
    startMapsOnce();
  } else {
    // Le SDK Axeptio consomme cette file dès qu'il est prêt (gère le cas où il
    // se charge après ce script). cf. doc Axeptio "consentement contextuel".
    window._axcb = window._axcb || [];
    window._axcb.push((sdk) => {
      sdk.on('cookies:complete', (choices) => {
        if (choices && choices[GMAPS_VENDOR]) {
          startMapsOnce();
        } else if (consentOverlay && !mapsStarted) {
          consentOverlay.style.display = 'flex';
        }
      });
    });
  }
});